import extend from 'dva-model-extend';
import _ from 'lodash';

import { queryPersistence } from '@kc/gbiz-base/lib/QueryPersistence';
import { CAPTCHA_CODE, UTM_RCODE_MAP } from '../common/constants';
import * as services from './service';

import captchaModel from '../components/HumanCaptcha/model';

import {
  EMAIL_BIZTYPE,
  LOCAL_EMAIL_TIME_KEY,
  PHONE_BIZTYPE,
  LOCAL_PHONE_TIME_KEY,
  NAMESPACE,
} from './constants';

import {
  loopCrypto,
  resolveContryCode,
  removeSpaceSE,
  transformObjWithMap,
  kcsensorsManualTrack,
  sensorsLogin,
  getAnonymousID,
} from '../common/tools';

const bizTypeMap = {
  [EMAIL_BIZTYPE]: {
    localStorageTimeKey: LOCAL_EMAIL_TIME_KEY,
    servicesFn: services.sendEmailVerifyCode,
  },
  [PHONE_BIZTYPE]: {
    localStorageTimeKey: LOCAL_PHONE_TIME_KEY,
    servicesFn: services.sendPhoneVerifyCode,
  },
};

const initialValue = {
  isCount: false,
  loading: false,
  isCodeRegx: false,
  [`${PHONE_BIZTYPE}_countTime`]: {},
  [`${EMAIL_BIZTYPE}_countTime`]: {},
  bizType: PHONE_BIZTYPE,
  countryCodes: [],
  sendChannel: 'SMS',
  emailSuffixes: [],
  FirstStepData: {},
  registerType: 'email',
  abDefault: undefined,
  isNew: false,
  sendCodeSuc: false,
};

export default extend(captchaModel, {
  namespace: NAMESPACE,
  state: initialValue,
  reducers: {
    update(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },
  effects: {
    // 发送验证码
    *sendVerifyCode({ payload }, { call, put }) {
      const { toast, bizType, sendChannel, ...others } = payload;
      const { servicesFn, localStorageTimeKey } = bizTypeMap[bizType];
      localStorage.setItem(localStorageTimeKey, +new Date());
      yield put({ type: 'update', payload: { loading: true, sendCodeSuc: false } });
      try {
        const result = yield call(servicesFn, { sendChannel, ...others });
        const { data: { maxRetryTimes, retryTimes, retryAfterSeconds } = {}, msg, code } = result;
        // 发送验证码，如果达到最大次数，则给出一个提示
        if (maxRetryTimes === retryTimes) {
          toast.warning(msg);
        }
        if (code === '200') {
          yield put({
            type: 'update',
            payload: {
              sendChannel,
              isCount: true,
              sendCodeSuc: true,
              [`${bizType}_countTime`]: { time: retryAfterSeconds },
            },
          });
          toast.success(msg);
        }
      } catch (err) {
        if (err.code && err.code === CAPTCHA_CODE) {
          yield put({
            type: 'captchaInit',
            payload: {
              bizType,
              onCallBack: {
                type: 'sendVerifyCode',
                payload,
              },
            },
          });
        } else {
          const msg = _.isObject(err) ? JSON.stringify(err.msg) : err;
          toast.error(msg);
        }
      } finally {
        yield put({ type: 'update', payload: { loading: false } });
      }
    },

    // 校验验证码发起注册flow
    *signUpFlow({ payload, trackResultParams = {} }, { put, take, select }) {
      yield put({ type: 'verifyCode', payload });
      yield take('verifyCode/@@end');
      const { isCodeRegx } = yield select((state) => state[NAMESPACE]);
      if (isCodeRegx) {
        return yield put({ type: 'signUp', payload, trackResultParams });
      }
      return false;
    },

    // 验证验证码
    *verifyCode({ payload }, { call, put, select }) {
      const { sendChannel } = yield select((state) => state[NAMESPACE]);
      const { code, validationType, lang, toast, countryCode, phone, email } = payload;
      const checkType = validationType === 'PHONE' ? sendChannel || 'SMS' : 'EMAIL';
      const address =
        validationType === 'PHONE'
          ? `${resolveContryCode(countryCode)}-${removeSpaceSE(phone)}`
          : removeSpaceSE(email);
      try {
        const { code: dataCode, msg } = yield call(services.verifyCode, {
          address,
          code: removeSpaceSE(code),
          validationType: checkType,
          lang,
        });
        if (dataCode !== '200') {
          toast.error(msg);
          yield put({ type: 'update', payload: { isCodeRegx: false } });
          return;
        }
        // 更新codeRegx
        yield put({ type: 'update', payload: { isCodeRegx: true } });
      } catch (err) {
        yield put({ type: 'update', payload: { isCodeRegx: false } });
        const msg = _.isObject(err) ? JSON.stringify(err.msg) : err;
        toast.error(msg);
      }
    },

    // 发起注册
    *signUp({ payload, trackResultParams = {} }, { call, put }) {
      const {
        email,
        code,
        validationType,
        phone,
        countryCode,
        password,
        toast,
        t,
        isKumex,
        referralCode,
      } = payload;
      try {
        // 从localstorage里面取rcode, utm_source, utm_campaign, utm_medium
        const persistenceQuery = queryPersistence.getPersistenceQuery();

        const utmAndRcodeOption = transformObjWithMap(persistenceQuery, UTM_RCODE_MAP);

        let option = {
          code,
          password: loopCrypto(password, 2),
          timeZone: new Date().getTimezoneOffset() * 60,
          ...utmAndRcodeOption,
          anonymousId: getAnonymousID(),
        };

        if (isKumex) {
          option.thirdPartReferralCode = 'kumex';
        }

        let signUpFn;

        if (validationType === 'PHONE') {
          option = {
            referralCode: removeSpaceSE(referralCode),
            ...option,
            countryCode: resolveContryCode(countryCode),
            phone: removeSpaceSE(phone),
          };
          signUpFn = services.signUpPhone;
        } else {
          option = {
            referralCode: removeSpaceSE(referralCode),
            ...option,
            email: removeSpaceSE(email),
          };
          signUpFn = services.signUpEmail;
        }

        const { data, code: dataCode, msg } = yield call(signUpFn, option);
        if (dataCode !== '200') {
          // 注册失败埋点
          kcsensorsManualTrack(
            {
              checkID: false,
              data: {
                ...trackResultParams,
                is_success: false,
                fail_reason: msg,
                fail_reason_code: dataCode,
              },
            },
            'register_result',
          );
          toast.error(msg);
          return false;
        }
        // 注册成功后，会自动登录，故这里需要调用sdk的login方法
        sensorsLogin(String(data && data.uid), String(data && data.honorLevel));
        // 注册成功埋点
        kcsensorsManualTrack(
          {
            checkID: false,
            data: {
              ...trackResultParams,
              is_success: true,
            },
          },
          'register_result',
        );
        // 注册成功后，会自动登录，故这里需要上报一次login_result 事件
        const loginSuccessData = {
          source: trackResultParams.source,
          is_success: true,
          is_first_time: true,
        };
        kcsensorsManualTrack(
          {
            checkID: false,
            data: loginSuccessData,
          },
          'login_result',
        );
        toast.info(t('sign_success'));
        // 成功之后，重置为初始值
        yield put({
          type: 'resetInit',
        });
        return data;
      } catch (err) {
        const msg = _.isObject(err) ? JSON.stringify(err.msg) : err;
        toast.error(msg);
        // 注册失败-埋点
        kcsensorsManualTrack(
          {
            checkID: false,
            data: {
              ...trackResultParams,
              is_success: false,
              fail_reason: msg,
              fail_reason_code: _.isObject(err) ? JSON.stringify(err.code) || '' : '',
            },
          },
          'register_result',
        );
        return false;
      }
    },

    // 获取区号
    *getCountryCodes(_, { call, put }) {
      const { data } = yield call(services.getCountryCodes);
      yield put({
        type: 'update',
        payload: {
          countryCodes: data,
        },
      });
    },

    // 重置所有参数
    *resetInit(_, { put }) {
      yield put({
        type: 'update',
        payload: initialValue,
      });
    },

    // 邮箱召回
    *postEmailRecall({ payload }, { call }) {
      try {
        yield call(services.postEmailRecall, payload);
      } catch (e) {
        const { msg } = e || {};
        console.error('msg', msg);
      }
    },

    // 手机召回
    *postPhoneRecall({ payload }, { call }) {
      try {
        yield call(services.postPhoneRecall, payload);
      } catch (e) {
        const { msg } = e || {};
        console.error('msg', msg);
      }
    },

    // 获取邮箱后缀
    *getEmailSuffixes(_, { call, put }) {
      const { data } = yield call(services.getEmailSuffixes);
      yield put({
        type: 'update',
        payload: {
          emailSuffixes: data || [],
        },
      });
    },
    // 获取邮箱后缀
    *getSensorsABDefault(_, { call, put }) {
      const { data } = yield call(services.getSensorsABDefault);
      yield put({
        type: 'update',
        payload: {
          abDefault: data,
        },
      });
      return data;
    },
  },
});
